import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';

type SeekRangeProps = {
  /** Playback position in the same units as min/max (seconds or 0–100). */
  value: number;
  min?: number;
  max: number;
  step?: number;
  className?: string;
  'aria-label'?: string;
  disabled?: boolean;
  /**
   * When false, only seek on pointer-up / keyboard commit (avoids HLS skip
   * thrash while dragging). Visual scrub still updates live. Default true.
   */
  liveSeek?: boolean;
  /** Live seek while dragging (audio/video currentTime), or on commit only. */
  onSeek: (next: number) => void;
  /** True while the pointer is down on the slider — gate external time ticks. */
  onScrubbingChange?: (scrubbing: boolean) => void;
};

/**
 * Range input that keeps a local scrub value while dragging so timeupdate
 * cannot fight the thumb. Uses pointer + input events for snappy touch/mouse.
 */
export function SeekRange({
  value,
  min = 0,
  max,
  step = 0.1,
  className,
  'aria-label': ariaLabel = 'Seek',
  disabled,
  liveSeek = true,
  onSeek,
  onScrubbingChange,
}: SeekRangeProps) {
  const [scrub, setScrub] = useState<number | null>(null);
  const scrubbingRef = useRef(false);
  const safeMax = Number.isFinite(max) && max > min ? max : min + 1;
  const display = scrub ?? value;
  const clamped = Math.min(safeMax, Math.max(min, display));
  const progressPct =
    ((clamped - min) / Math.max(1e-6, safeMax - min)) * 100;

  const setScrubbing = useCallback(
    (next: boolean) => {
      if (scrubbingRef.current === next) return;
      scrubbingRef.current = next;
      onScrubbingChange?.(next);
    },
    [onScrubbingChange],
  );

  const apply = useCallback(
    (raw: number, { commit }: { commit: boolean }) => {
      if (!Number.isFinite(raw)) return;
      const next = Math.min(safeMax, Math.max(min, raw));
      setScrub(next);
      if (liveSeek || commit) onSeek(next);
      if (commit) {
        // Keep local thumb until the controlled value catches the commit —
        // clearing scrub immediately lets a stale tick snap the bar back.
        // Parent timeupdate is separately gated by pending-seek in usePlayer.
        setScrubbing(false);
      }
    },
    [liveSeek, min, onSeek, safeMax, setScrubbing],
  );

  // Drop local scrub once the parent value matches — grace timeout if seek is a no-op.
  useEffect(() => {
    if (scrubbingRef.current || scrub == null) return;
    if (Math.abs(value - scrub) < Math.max(step, 1e-3)) {
      setScrub(null);
      return;
    }
    const t = window.setTimeout(() => {
      if (!scrubbingRef.current) setScrub(null);
    }, 450);
    return () => window.clearTimeout(t);
  }, [value, max, scrub, step]);

  useEffect(
    () => () => {
      if (scrubbingRef.current) setScrubbing(false);
    },
    [setScrubbing],
  );

  const onPointerDown = (e: ReactPointerEvent<HTMLInputElement>) => {
    if (disabled) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    // Arm scrub only — do not seek with the pre-click thumb value (that skips).
    setScrubbing(true);
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLInputElement>) => {
    if (disabled) return;
    apply(Number(e.currentTarget.value), { commit: true });
  };

  return (
    <input
      type="range"
      className={className}
      aria-label={ariaLabel}
      disabled={disabled}
      min={min}
      max={safeMax}
      step={step}
      value={clamped}
      style={{ '--seek-progress': `${progressPct}%` } as CSSProperties}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      onInput={(e) => {
        apply(Number(e.currentTarget.value), { commit: false });
      }}
      onChange={(e) => {
        // Keyboard / accessibility path — commit immediately.
        apply(Number(e.currentTarget.value), { commit: !scrubbingRef.current });
      }}
    />
  );
}
